import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Prediction } from '../../types/predictions';

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
  flex-wrap: wrap;
`;

const ScoreDot = styled.div<{ $status: 'success' | 'miss' | 'updated' | 'pending' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => 
    props.$status === 'success' ? '#22C55E' : // Green for success
    props.$status === 'miss' ? '#EF4444' :    // Red for miss
    props.$status === 'updated' ? '#8B5CF6' : // Purple for updated
    '#FF9800'                                 // Orange for pending
  };
  opacity: 0.9;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

interface InfluencerScoreDotsProps {
  influencerName: string;
  influencerHistory?: Prediction[]; // ⚡ Pre-computed history for performance (mobile)
  allPredictions?: Prediction[]; // Legacy prop for backward compatibility (desktop)
  maxDots?: number;
}

const InfluencerScoreDots: React.FC<InfluencerScoreDotsProps> = ({ 
  influencerName, 
  influencerHistory, 
  allPredictions,
  maxDots = 10 
}) => {
  
  // Handle both new (pre-computed) and legacy (computed on-demand) props
  const finalHistory = useMemo(() => {
    // ⚡ FAST PATH: Use pre-computed history if available (mobile portrait)
    if (influencerHistory && influencerHistory.length > 0) {
      return influencerHistory;
    }
    
    // LEGACY PATH: Compute on demand if allPredictions is provided (desktop)
    if (allPredictions && allPredictions.length > 0) {
      const influencerPredictions = allPredictions
        .filter(prediction => {
          const predictionName = prediction.influencer?.name?.trim();
          const searchName = influencerName?.trim();
          return predictionName && searchName && predictionName === searchName;
        })
        .sort((a, b) => {
          const aDate = new Date(a.prediction_made || a.created_at || a.end_date || 0);
          const bDate = new Date(b.prediction_made || b.created_at || b.end_date || 0);
          return aDate.getTime() - bDate.getTime();
        })
        .slice(-maxDots);
      
      return influencerPredictions;
    }
    
    return [];
  }, [influencerHistory, allPredictions, influencerName, maxDots]);

  // Don't show anything if no predictions
  if (finalHistory.length === 0) {
    return null;
  }

  return (
    <ScoreContainer>
      {finalHistory.map((prediction, index) => (
        <ScoreDot 
          key={`${prediction.id}-${index}`}
          $status={(prediction.success_status || 'pending') as 'success' | 'miss' | 'updated' | 'pending'}
          title={`${(prediction.success_status || 'pending').toUpperCase()}: ${prediction.prediction_made ? new Date(prediction.prediction_made).toLocaleDateString() : 'Date unknown'}`}
        />
      ))}
    </ScoreContainer>
  );
};

export default InfluencerScoreDots;